import * as React from "react";
import { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link } from "react-router-dom";
import img4 from '../assets/blog4.png';

export default function BlogPostPage4() {
  const theme = useTheme();

  // ✅ Scroll to top on load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const post = {
    title: "Main Reasons To Fast Business Builder",
    author: "Sudhanshu",
    date: "December 23, 2024",
    heroImg:img4,
     
    intro:
      "Fast business building is not about rushing decisions, but about creating strong systems, clear direction, and efficient execution. Businesses that grow faster usually focus on structure, clarity, and repeatable processes rather than random efforts.",
    sections: [
      {
        title: "Why Fast Business Building Matters",
        bullets: [
          "Clear systems reduce confusion and save time across teams.",
          "Structured execution improves consistency and reliability.",
          "Faster decision-making leads to quicker market response.",
        ],
      },
      {
        title: "Core Drivers of Rapid Growth",
        bullets: [
          "Well-defined processes that can scale with business growth.",
          "Clear roles, responsibilities, and accountability.",
          "Data-driven decisions supported by measurable KPIs.",
        ],
      },
      {
        title: "Execution & Discipline",
        bullets: [
          "Consistent execution is more important than complex planning.",
          "Focus on priorities instead of multitasking.",
          "Continuous review and optimization of workflows.",
        ],
      },
      {
        title: "Quick Takeaways",
        bullets: [
          "Build repeatable systems before aggressive scaling.",
          "Keep operations simple and measurable.",
          "Optimize continuously for long-term sustainable growth.",
        ],
      },
    ],
    conclusion:
      "A fast business builder approach emphasizes clarity, systems, and execution. When businesses focus on repeatable workflows and disciplined action, growth becomes predictable, scalable, and sustainable over time.",
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f9fafb", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.2, sm: 3 },
            borderRadius: 4,
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 14px 40px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
          }}
        >
          {/* ================= HEADER ================= */}
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={900}>
              {post.title}
            </Typography>

            <Stack direction="row" spacing={2} color="text.secondary">
              <Stack direction="row" spacing={0.6} alignItems="center">
                <PersonOutlineIcon fontSize="small" />
                <Typography variant="body2">{post.author}</Typography>
              </Stack>

              <Stack direction="row" spacing={0.6} alignItems="center">
                <CalendarMonthIcon fontSize="small" />
                <Typography variant="body2">{post.date}</Typography>
              </Stack>
            </Stack>
          </Stack>

          {/* ================= HERO IMAGE ================= */}
          <Grid container spacing={2.5} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3} />
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 240, sm: 300, md: 360 },
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.1)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  component="img"
                  src={post.heroImg}
                  alt={post.title}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3} />
          </Grid>

          {/* ================= OVERVIEW ================= */}
          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 10px 26px rgba(0,0,0,0.05)",
              }}
            >
              <Typography variant="h6" fontWeight={900} mb={1}>
                Overview
              </Typography>

              <Typography
                variant="body1"
                sx={{ lineHeight: 1.9, color: "text.secondary" }}
              >
                {post.intro}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
                <Chip label="Business Growth" size="small" />
                <Chip label="Execution" size="small" />
                <Chip label="Scalability" size="small" />
                <Chip label="Strategy" size="small" />
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* ================= CONTENT ================= */}
          <Stack spacing={2.2}>
            {post.sections.map((sec, idx) => (
              <Box key={sec.title}>
                <Typography variant="h6" fontWeight={900} mb={1}>
                  {idx + 1}. {sec.title}
                </Typography>

                <List disablePadding>
                  {sec.bullets.map((b) => (
                    <ListItemButton
                      key={b}
                      disableRipple
                      sx={{
                        px: 0,
                        py: 0.6,
                        cursor: "default",
                        "&:hover": { bgcolor: "transparent" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ lineHeight: 1.85 }}>
                            • {b}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* ================= CONCLUSION ================= */}
          <Box
            sx={{
              p: 2.2,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            }}
          >
            <Typography variant="h6" fontWeight={900} mb={1}>
              Conclusion
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
              {post.conclusion}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* ================= PREVIOUS / NEXT ================= */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Link to="/BlogPostPage3" style={{ textDecoration: "none" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackRoundedIcon />}
                  sx={{
                    justifyContent: "flex-start",
                    borderRadius: 3,
                    py: 1.4,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Previous Post
                </Button>
              </Link>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  justifyContent: "flex-end",
                  borderRadius: 3,
                  py: 1.4,
                  fontWeight: 800,
                  textTransform: "none",
                }}
                component={Link}
                to="/BlogPostPage5"
              >
                Next Post
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}
